import { 
  AUTH_VALIDATION_LIMITS, 
  AUTH_ERROR_MESSAGES,
  USER_ROLES 
} from '../constants/authConstants';

// Basic validation utilities
export const validateRequired = (value, fieldName = 'Field') => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateEmail = (email) => {
  if (!email) {
    return AUTH_ERROR_MESSAGES.REQUIRED_FIELD;
  }
  
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email)) {
    return AUTH_ERROR_MESSAGES.INVALID_EMAIL;
  }
  
  return null;
};

export const validatePassword = (password) => {
  if (!password) {
    return AUTH_ERROR_MESSAGES.REQUIRED_FIELD;
  }
  
  if (password.length < AUTH_VALIDATION_LIMITS.PASSWORD_MIN_LENGTH) {
    return AUTH_ERROR_MESSAGES.WEAK_PASSWORD;
  }
  
  return null;
};

export const validateConfirmPassword = (password, confirmPassword) => {
  if (!confirmPassword) {
    return AUTH_ERROR_MESSAGES.REQUIRED_FIELD;
  }
  
  if (password !== confirmPassword) {
    return AUTH_ERROR_MESSAGES.PASSWORD_MISMATCH;
  }
  
  return null;
};

export const validateName = (name, fieldName = 'Name') => {
  if (!name || !name.trim()) {
    return `${fieldName} is required`;
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length < AUTH_VALIDATION_LIMITS.NAME_MIN_LENGTH) {
    return `${fieldName} must be at least ${AUTH_VALIDATION_LIMITS.NAME_MIN_LENGTH} characters`;
  }
  
  if (trimmedName.length > AUTH_VALIDATION_LIMITS.NAME_MAX_LENGTH) {
    return `${fieldName} must not exceed ${AUTH_VALIDATION_LIMITS.NAME_MAX_LENGTH} characters`;
  }
  
  return null;
};

export const validateSignUpForm = (formData) => {
  const errors = {};
  
  // Full Name validation
  const fullNameError = validateName(formData.fullName, 'Full name');
  if (fullNameError) errors.fullName = fullNameError;
  
  // Email validation
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  // Password validation
  const passwordError = validatePassword(formData.password);
  if (passwordError) errors.password = passwordError;
  
  // Confirm Password validation
  const confirmPasswordError = validateConfirmPassword(formData.password, formData.confirmPassword);
  if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;
  
  // Sector validation
  if (!formData.sector) {
    errors.sector = 'Sector is required';
  }
  
  // Department validation
  if (!formData.department) {
    errors.department = 'Department is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Login form validation
export const validateLoginForm = (formData) => {
  const errors = {};
  
  // Email validation
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  // Password validation (just check if provided)
  if (!formData.password) {
    errors.password = AUTH_ERROR_MESSAGES.REQUIRED_FIELD;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Forgot password form validation
export const validateForgotPasswordForm = (formData) => {
  const errors = {};
  
  // Email validation
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateAdminAddUserForm = (formData) => {
  const errors = {};
  
  // Full Name validation
  const fullNameError = validateName(formData.fullName, 'Full name');
  if (fullNameError) errors.fullName = fullNameError;
  
  // Email validation
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  // Sector validation (only for department users)
  if (formData.role === USER_ROLES.DEPARTMENT && !formData.sector) {
    errors.sector = 'Sector is required for department users';
  }
  
  // Department validation (only for department users)
  if (formData.role === USER_ROLES.DEPARTMENT && !formData.department) {
    errors.department = 'Department is required for department users';
  }
  
  // Role validation
  if (!formData.role) {
    errors.role = 'Role is required';
  }
  
  // Validate role is one of the allowed roles
  if (formData.role && !Object.values(USER_ROLES).includes(formData.role)) {
    errors.role = 'Invalid role selected';
  }
  
  // Status validation
  if (!formData.status) {
    errors.status = 'Status is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const sanitizeUserData = (userData) => {
  const sanitized = { ...userData };
  
  // Trim string fields
  if (sanitized.fullName) sanitized.fullName = sanitized.fullName.trim();
  if (sanitized.email) sanitized.email = sanitized.email.toLowerCase().trim();
  if (sanitized.sector) sanitized.sector = sanitized.sector.trim();
  if (sanitized.department) sanitized.department = sanitized.department.trim();
  
  // Remove undefined or null values
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] === undefined || sanitized[key] === null) {
      delete sanitized[key];
    }
  });
  
  return sanitized;
};

// Default export
export default {
  validateRequired,
  validateEmail,
  validatePassword,
  validateConfirmPassword,
  validateName,
  validateSignUpForm,
  validateLoginForm,
  validateForgotPasswordForm,
  validateAdminAddUserForm,
  sanitizeUserData
};